import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import config from '../config';
import { getIdToken } from '../attendedUtils';
import { ZoomHint, ImageLightbox } from './ZoomableImage';
import { useMediaQuery, useIsMobile, useIsTablet } from '../hooks/useMediaQuery';
import { QuickCreateArtistModal } from './AddEvent';
import { VISUAL_ARTIST_TYPE } from '../artistStyleUtils';

const API = config.API_URL;
const MAX_FILE_BYTES = 4 * 1024 * 1024; // 4 MB
const ENTITY_TYPES = ['artist', 'event', 'festival', 'member', 'promoter', 'venue'];
const EVENT_FILTER_TYPES = ['artist', 'festival', 'promoter', 'venue'];
const PAGE_SIZE = 50; // My Images Search spec §5.4

// Normalize for search: strip diacritics and punctuation so "motorhead"
// matches "Motörhead" and "carissas" matches "Carissa's Wierd".
// Copied verbatim from ArtistList.js (Browse Artists) — keep in sync.
const normalizeStr = (str) =>
    (str || '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/['‘’‛`\-.,:!?&]/g, '')
        .toLowerCase();

// ── Search haystack (spec §7.1) ──────────────────────────────────────────────
// One normalized string per image: caption, assigned entity name, and for
// every linked event (from getImages.js /mine → linked_events) the full
// lineup (headliners + support), venue, city/state/country, stage, festival,
// promoters, and the event date in ISO / short / long forms so "2019" or
// "october 2019" match.
function buildHaystack(img) {
    // 2026-10-12: credited and pictured artist names are searchable too
    const parts = [img.caption, img.entity_name, img.credit_artist_name, img.photo_artist_name];
    (img.linked_events || []).forEach(ev => {
        parts.push(...(ev.artists || []));
        parts.push(ev.venue_name, ev.city, ev.state, ev.country, ev.stage_name, ev.festival_name);
        parts.push(...(ev.promoters || []));
        if (ev.event_date) {
            const d = new Date(ev.event_date);
            parts.push(String(ev.event_date).slice(0, 10));
            parts.push(d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' }));
            parts.push(d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' }));
        }
    });
    return normalizeStr(parts.filter(Boolean).join(' | '));
}

// ── ZoomablePreview ───────────────────────────────────────────────────────────
// Image thumbnail with a hover + cursor that opens the shared full-screen
// lightbox (with magnifier). Used in both UploadModal and EditModal wizard
// so the preview is always readable.
function ZoomablePreview({ src, alt = '', style = {} }) {
    const [hovered, setHovered] = useState(false);
    const [expanded, setExpanded] = useState(false);

    return (
        <>
            <div
                style={{ position: 'relative', cursor: 'zoom-in', display: 'inline-block', width: '100%' }}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                onClick={() => setExpanded(true)}
            >
                <img
                    src={src}
                    alt={alt}
                    style={{
                        width: '100%',
                        borderRadius: '8px',
                        objectFit: 'contain',
                        background: '#111',
                        display: 'block',
                        transition: 'opacity 0.15s ease',
                        opacity: hovered ? 0.82 : 1,
                        ...style,
                    }}
                />
                <ZoomHint visible={hovered} />
            </div>

            {expanded && (
                <ImageLightbox
                    src={src}
                    alt={alt}
                    onClose={() => setExpanded(false)}
                />
            )}
        </>
    );
}

// Upload targets (2026-10-12, SHARC_Spec_Visual_Artists_Image_Credits §4.1).
// key = images.image_kind; createImage / updateImage validate the shape.
const UPLOAD_TARGETS = [
    { key: 'event_flyer_single',   label: 'Event Flyer – Single',   hint: 'Show Flyer, Show Poster' },
    { key: 'event_flyer_multiple', label: 'Event Flyer – Multiple', hint: 'Artist Tour Poster, Festival Poster, Venue Show Calendar or Listing' },
    { key: 'event_photo_artist',   label: 'Event Photo – Artist',   hint: 'Artist Live Photo' },
    { key: 'non_event',            label: 'Non-Event Image',        hint: 'Promo Photo, Logos, etc.' },
];
const TARGET_LABEL = Object.fromEntries(UPLOAD_TARGETS.map(t => [t.key, t.label]));
const EVENT_KINDS = ['event_flyer_single', 'event_flyer_multiple', 'event_photo_artist'];
const NON_EVENT_ENTITY_TYPES = ['artist', 'festival', 'member', 'promoter', 'venue'];

// ── Helpers ───────────────────────────────────────────────────────────────────
// Card credit / pictured line (2026-10-19): artist name as a link
function ArtistNameLink({ name, slug, id }) {
    const to = slug || id ? `/artists/${slug || id}` : null;
    return to
        ? <Link to={to} style={{ color: '#bb86fc', textDecoration: 'none' }} onClick={e => e.stopPropagation()}>{name}</Link>
        : <>{name}</>;
}

function formatDate(iso) {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function entityLabel(type) {
    if (!type) return '—';
    return type.charAt(0).toUpperCase() + type.slice(1);
}

// ── Entity Search ─────────────────────────────────────────────────────────────
// Calls GET /search?q=<typed>&type=<entityType> after 350ms debounce,
// shows a dropdown of up to 10 matching records, user clicks to select one.
// excludeParents: when true (festival only), omits festival series from
// results — used when searching "by festival" to find events, since a
// series has no events of its own. Leave unset for direct-entity search.
// artistFilter (artist only, 2026-10-12): 'visual' = Visual Artists only
// (Credits step), 'performers' = excludes Visual Artists.
// onCreateItem(query) (2026-10-15, Credits quick create): when given, every
// results / no-results dropdown leads with a "＋ <createLabel>: "<query>""
// row — the AddEvent SearchableSelect pattern.
function EntitySearch({ entityType, value, onChange, excludeParents = false, artistFilter = '', placeholder, onCreateItem, createLabel = 'Create new' }) {
    const [query, setQuery]       = useState(value?.name || '');
    const [results, setResults]   = useState([]);
    const [searching, setSearching] = useState(false);
    const [open, setOpen]         = useState(false);
    const [selected, setSelected] = useState(value || null);
    const timerRef    = useRef(null);
    const containerRef = useRef(null);

    // Reset when entityType changes — but not on mount, so a pre-filled value
    // (e.g. an image's existing credit, or a search kept when going Back)
    // survives the component mounting.
    const mountedRef = useRef(false);
    useEffect(() => {
        if (!mountedRef.current) { mountedRef.current = true; return; }
        setQuery(''); setResults([]); setSelected(null); setOpen(false);
        onChange(null);
    }, [entityType]); // eslint-disable-line react-hooks/exhaustive-deps

    // Close dropdown on outside click
    useEffect(() => {
        const onOut = e => { if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', onOut);
        return () => document.removeEventListener('mousedown', onOut);
    }, []);

    const runSearch = useCallback(async (q) => {
        if (!q || q.trim().length < 2) { setResults([]); setOpen(false); return; }
        setSearching(true);
        try {
            const token = await getIdToken();
            const res = await axios.get(`${API}/search`, {
                params: {
                    q: q.trim(),
                    type: entityType,
                    ...(excludeParents ? { excludeParents: 'true' } : {}),
                    ...(entityType === 'artist' && artistFilter ? { artistFilter } : {}),
                },
                headers: { Authorization: token },
            });
            setResults(res.data.results || []);
            setOpen(true);
        } catch {
            setResults([]);
        } finally { setSearching(false); }
    }, [entityType, excludeParents, artistFilter]);

    const handleChange = (e) => {
        const v = e.target.value;
        setQuery(v);
        setSelected(null); onChange(null);
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => runSearch(v), 350);
    };

    const handleSelect = (r) => {
        setSelected(r); setQuery(r.name); setOpen(false);
        onChange({ id: r.id, name: r.name });
    };

    const handleClear = () => {
        setQuery(''); setSelected(null); setResults([]); setOpen(false);
        onChange(null);
    };

    const createRow = onCreateItem && (
        <div
            onMouseDown={e => { e.preventDefault(); setOpen(false); onCreateItem(query.trim()); }}
            style={{ padding: '9px 12px', fontSize: '13px', color: '#bb86fc', cursor: 'pointer', borderBottom: '1px solid #3a3a3a', display: 'flex', alignItems: 'center', gap: '6px' }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#2a2040'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
        >
            <span style={{ fontSize: '15px', lineHeight: 1 }}>&#xFF0B;</span>
            <span>{createLabel}{query.trim() ? `: "${query.trim()}"` : ''}</span>
        </div>
    );

    return (
        <div ref={containerRef} style={{ position: 'relative' }}>
            <div style={{ position: 'relative' }}>
                <input
                    type="text"
                    value={query}
                    onChange={handleChange}
                    onFocus={() => results.length > 0 && !selected && setOpen(true)}
                    placeholder={placeholder || `Search ${entityLabel(entityType)}s by name…`}
                    style={{ ...S.input, paddingRight: selected ? '32px' : '10px' }}
                />
                {searching && (
                    <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '12px', color: '#888' }}>…</span>
                )}
                {selected && !searching && (
                    <button onClick={handleClear} style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontSize: '14px', lineHeight: 1 }} title="Clear">✕</button>
                )}
            </div>

            {/* Results dropdown */}
            {open && results.length > 0 && !selected && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#2c2c2c', border: '1px solid #444', borderTop: 'none', borderRadius: '0 0 6px 6px', zIndex: 500, maxHeight: '220px', overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.5)' }}>
                    {createRow}
                    {results.map(r => (
                        <div
                            key={r.id}
                            onMouseDown={() => handleSelect(r)}
                            style={{ padding: '9px 12px', borderBottom: '1px solid #333', cursor: 'pointer' }}
                            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#2a2040'}
                            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                            <div style={{ fontSize: '13px', color: '#e0e0e0' }}>{r.name}</div>
                            {r.sub && <div style={{ fontSize: '11px', color: '#777', marginTop: '2px' }}>{r.sub}</div>}
                        </div>
                    ))}
                </div>
            )}

            {/* No results message */}
            {open && results.length === 0 && !searching && query.trim().length >= 2 && !selected && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#2c2c2c', border: '1px solid #444', borderTop: 'none', borderRadius: '0 0 6px 6px', zIndex: 500, fontSize: '12px', color: '#888', boxShadow: '0 8px 24px rgba(0,0,0,0.5)' }}>
                    {createRow}
                    <div style={{ padding: '10px 12px' }}>No {entityLabel(entityType)}s found for "{query}"</div>
                </div>
            )}

            {/* Selected confirmation */}
            {selected && (
                <div style={{ marginTop: '5px', fontSize: '11px', color: '#40916c' }}>
                    ✓ {selected.name}{selected.sub ? ` — ${selected.sub}` : ''}
                </div>
            )}
        </div>
    );
}

// ── Event Results Table ───────────────────────────────────────────────────────
// Shared by Step 4 in both the global wizard and the per-entity Add Photo wizard.
// isMultiple: if true, checkboxes allow multi-select; if false, selecting one clears others.
// byVenue: if true, shows Date/Lineup/Stage columns; if false, shows Date/Venue/City/State/Country/Lineup.
// onSelectAll(checked) (2026-10-12): when given with isMultiple, the header's
// first column becomes a "Select All" checkbox over the listed events.
function EventResultsTable({ events, selectedIds, onToggle, isMultiple, byVenue, onSelectAll }) {
    const isMobile = useIsMobile();
    const formatEventDate = d => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '—';

    if (events.length === 0) {
        return <div style={{ color: '#666', fontSize: '13px', padding: '12px 0' }}>No events found.</div>;
    }

    const listedSelected = events.filter(e => selectedIds.includes(e.event_id)).length;
    const allListedSelected  = events.length > 0 && listedSelected === events.length;
    const someListedSelected = listedSelected > 0;

    const gridCols = byVenue
        ? '24px 110px 1fr 100px'          // ☐ Date Lineup Stage
        : '24px 110px 1fr 70px 60px 60px 1.2fr'; // ☐ Date Venue City State Country Lineup

    // Mobile (spec §8.2): the multi-column grid scrolls sideways inside its
    // border rather than crushing below ~620px.
    return (
        <div style={{ border: '1px solid #333', borderRadius: '6px', overflow: 'hidden', fontSize: '12px', marginTop: '8px', ...(isMobile ? Smobile.eventTableOuter : {}) }}>
          <div style={isMobile ? Smobile.eventTableInner : undefined}>
            {/* Header */}
            <div style={{ display: 'grid', gridTemplateColumns: gridCols, gap: '6px', padding: '7px 10px', background: '#252525', color: '#888', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.4px', borderBottom: '1px solid #333', alignItems: 'center' }}>
                {isMultiple && onSelectAll ? (
                    <input
                        type="checkbox"
                        title="Select All"
                        aria-label="Select all listed events"
                        checked={allListedSelected}
                        ref={el => { if (el) el.indeterminate = someListedSelected && !allListedSelected; }}
                        onChange={e => onSelectAll(e.target.checked)}
                        style={{ margin: 0, accentColor: '#bb86fc', cursor: 'pointer' }}
                    />
                ) : <span></span>}
                <span>Date</span>
                {byVenue ? (
                    <>
                        <span>Lineup</span>
                        <span>Stage</span>
                    </>
                ) : (
                    <>
                        <span>Venue</span>
                        <span>City</span>
                        <span>State</span>
                        <span>Country</span>
                        <span>Lineup</span>
                    </>
                )}
            </div>
            <div style={{ maxHeight: '260px', overflowY: 'auto' }}>
                {events.map(e => {
                    const isSelected = selectedIds.includes(e.event_id);
                    return (
                        <div
                            key={e.event_id}
                            onClick={() => onToggle(e, isMultiple)}
                            style={{
                                display: 'grid',
                                gridTemplateColumns: gridCols,
                                gap: '6px',
                                padding: '8px 10px',
                                borderBottom: '1px solid #222',
                                cursor: 'pointer',
                                backgroundColor: isSelected ? '#2a2040' : 'transparent',
                                color: isSelected ? '#bb86fc' : '#ccc',
                                alignItems: 'center',
                            }}
                            onMouseEnter={ev => { if (!isSelected) ev.currentTarget.style.backgroundColor = '#222'; }}
                            onMouseLeave={ev => { if (!isSelected) ev.currentTarget.style.backgroundColor = 'transparent'; }}
                        >
                            <input
                                type={isMultiple ? 'checkbox' : 'radio'}
                                checked={isSelected}
                                onChange={() => {}}
                                style={{ margin: 0, accentColor: '#bb86fc' }}
                            />
                            <span style={{ whiteSpace: 'nowrap' }}>{formatEventDate(e.event_date)}</span>
                            {byVenue ? (
                                <>
                                    <span style={{ color: isSelected ? '#bb86fc' : '#aaa', fontSize: '11px' }}>{e.artists || '—'}</span>
                                    <span style={{ color: isSelected ? '#bb86fc' : '#aaa' }}>{e.stage_name || '—'}</span>
                                </>
                            ) : (
                                <>
                                    <span>{e.venue_name || '—'}</span>
                                    <span style={{ color: isSelected ? '#bb86fc' : '#aaa' }}>{e.city || '—'}</span>
                                    <span style={{ color: isSelected ? '#bb86fc' : '#aaa' }}>{e.state || '—'}</span>
                                    <span style={{ color: isSelected ? '#bb86fc' : '#aaa' }}>{e.country || '—'}</span>
                                    <span style={{ color: isSelected ? '#bb86fc' : '#aaa', fontSize: '11px' }}>{e.artists || '—'}</span>
                                </>
                            )}
                        </div>
                    );
                })}
            </div>
            {isMultiple && selectedIds.length > 0 && (
                <div style={{ padding: '6px 10px', background: '#1e1a30', fontSize: '11px', color: '#bb86fc', borderTop: '1px solid #333' }}>
                    ✓ {selectedIds.length} event{selectedIds.length !== 1 ? 's' : ''} selected
                </div>
            )}
          </div>
        </div>
    );
}

// ── Step indicator (shared by both wizards) ───────────────────────────────────
// Pulled out of the two modals so the mobile single-line horizontal scroll
// (spec §8.2) lives in one place. displayStep is 0-based.
function StepIndicator({ labels, displayStep, isMobile }) {
    return (
        <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', alignItems: 'center', ...(isMobile ? Smobile.stepRow : {}) }}>
            {labels.map((label, i) => {
                const active = i === displayStep;
                const done   = i < displayStep;
                return (
                    <React.Fragment key={label}>
                        <div style={{
                            padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: done || active ? '600' : '400',
                            background: active ? '#bb86fc' : done ? '#2d6a4f' : '#2c2c2c',
                            color: active ? '#121212' : done ? '#40916c' : '#666',
                            border: `1px solid ${active ? '#bb86fc' : done ? '#40916c' : '#444'}`,
                            flexShrink: 0, whiteSpace: 'nowrap',
                        }}>
                            {done ? '✓' : `${i + 1}.`} {label}
                        </div>
                        {i < labels.length - 1 && <div style={{ width: '16px', height: '1px', background: '#333', flexShrink: 0 }} />}
                    </React.Fragment>
                );
            })}
        </div>
    );
}

// Modal shell styles — desktop widths unchanged; full-width, tighter padding
// on mobile (spec §8.2).
const modalStyle = (isMobile, maxWidth) => (isMobile
    ? { ...S.modal, ...Smobile.modal }
    : { ...S.modal, maxWidth, transition: 'max-width 0.2s ease' });
const overlayStyle = (isMobile) => (isMobile ? { ...S.overlay, ...Smobile.overlay } : S.overlay);

// ── Event loading for the Events step ────────────────────────────────────────
// Loads the events of the searched-by record and maps them to the
// EventResultsTable row shape. Shared by both wizards (unchanged mapping).
async function loadEventsFor(entityType, entityId) {
    const endpoint = entityType === 'artist' ? 'artists'
        : entityType === 'venue' ? 'venues'
        : entityType === 'festival' ? 'festivals'
        : 'promoters';
    const res = await axios.get(`${API}/${endpoint}/${entityId}`);
    if (entityType === 'artist' || entityType === 'venue') {
        return (res.data.show_history || []).map(e => ({
            event_id:   e.event_id,
            event_slug: e.event_slug,
            event_date: e.event_date,
            venue_name: e.venue_name,
            city:       e.city || e.venue_city,
            state:      e.state || e.venue_state,
            country:    e.country || e.venue_country,
            stage_name: e.stage_name,
            artists:    [e.headliners, e.support_acts].filter(Boolean).join(' · ') || e.other_artists || e.festival_name || '—',
        }));
    }
    if (entityType === 'festival') {
        return (res.data.events || []).map(e => ({
            event_id: e.event_id, event_slug: e.event_slug, event_date: e.event_date,
            venue_name: e.venue_name, city: e.city, state: e.state, country: e.country,
            artists: e.artists || '—',
        }));
    }
    return (res.data.events || []).map(e => ({
        event_id: e.event_id, event_slug: e.event_slug, event_date: e.event_date,
        venue_name: e.venue_name, city: e.venue_city, state: e.venue_state, country: e.venue_country,
        artists: e.artists || '—',
    }));
}

function formatEventLine(ev) {
    if (!ev) return '';
    return [formatDate(ev.event_date), ev.venue_name, [ev.city, ev.state].filter(Boolean).join(', ')].filter(Boolean).join(' · ');
}

// ── Assignment wizard state (shared by UploadModal and EditModal) ────────────
// Target → Search → Events → Credits, per SHARC_Spec_Visual_Artists_Image_Credits
// §4.1. Step numbers: 1 Target, 2 Search, 3 Events, 4 Credits, 5 Details
// (UploadModal adds 0 Upload in front).
//   event_flyer_single   — search events by Artist/Festival/Promoter/Venue, pick one
//   event_flyer_multiple — same search, pick many (Select All available)
//   event_photo_artist   — search events by Artist only, pick one; the searched
//                          artist becomes the pictured artist
//   non_event            — pick a record type, search for the record; no events
//   none                 — upload without assigning (UploadModal only)
function useAssignmentWizard({ initialTarget = '', initialCredit = null } = {}) {
    const [target, setTargetState]            = useState(initialTarget);
    const [filterEntityType, setFilterEntityType] = useState(EVENT_FILTER_TYPES[0]);
    const [filterEntity, setFilterEntity]     = useState(null);   // { id, name } — searched-by record
    const [directEntityType, setDirectEntityType] = useState(NON_EVENT_ENTITY_TYPES[0]);
    const [directEntity, setDirectEntity]     = useState(null);   // { id, name } — Non-Event record
    const [events, setEvents]                 = useState([]);
    const [eventsLoading, setEventsLoading]   = useState(false);
    const [eventsError, setEventsError]       = useState('');
    const [eventsRefine, setEventsRefine]     = useState('');
    const [selectedEventIds, setSelectedEventIds] = useState([]);
    const [creditMode, setCreditMode]         = useState(initialCredit ? 'assign' : 'none');
    const [creditArtist, setCreditArtist]     = useState(initialCredit); // { id, name }

    const isEventTarget = EVENT_KINDS.includes(target);
    const isMultiple    = target === 'event_flyer_multiple';
    const isPhoto       = target === 'event_photo_artist';
    // Event Photo – Artist searches by Artist only
    const searchType    = isPhoto ? 'artist' : filterEntityType;

    const setTarget = (t) => {
        setTargetState(t);
        setFilterEntity(null); setDirectEntity(null); setSelectedEventIds([]); setEvents([]); setEventsRefine('');
        if (t === 'event_photo_artist') setFilterEntityType('artist');
    };

    useEffect(() => {
        if (!isEventTarget || !filterEntity) { setEvents([]); return; }
        let cancelled = false;
        setEventsLoading(true); setEventsError(''); setEvents([]); setSelectedEventIds([]);
        loadEventsFor(searchType, filterEntity.id)
            .then(rows => { if (!cancelled) setEvents(rows); })
            .catch(() => { if (!cancelled) setEventsError('Failed to load events.'); })
            .finally(() => { if (!cancelled) setEventsLoading(false); });
        return () => { cancelled = true; };
    }, [isEventTarget, filterEntity, searchType]);

    const refineQ = eventsRefine.trim().toLowerCase();
    const filteredEvents = refineQ
        ? events.filter(e => [e.event_date, e.venue_name, e.city, e.state, e.country, e.artists]
            .some(v => v && String(v).toLowerCase().includes(refineQ)))
        : events;

    const toggleEvent = (e, multiple) => {
        if (multiple) setSelectedEventIds(prev => prev.includes(e.event_id) ? prev.filter(id => id !== e.event_id) : [...prev, e.event_id]);
        else setSelectedEventIds([e.event_id]);
    };
    // Select All acts on the events currently listed (after Refine)
    const selectAllListed = (checked) => {
        const listed = filteredEvents.map(e => e.event_id);
        setSelectedEventIds(prev => checked
            ? [...new Set([...prev, ...listed])]
            : prev.filter(id => !listed.includes(id)));
    };

    // Step path for the chosen target (Upload adds step 0 in front)
    const path = target === 'none' ? [1, 4, 5]
        : target === 'non_event' ? [1, 2, 4, 5]
        : [1, 2, 3, 4, 5];

    const canLeave = (step) => {
        if (step === 1) return !!target;
        if (step === 2) return target === 'non_event' ? !!directEntity : !!filterEntity;
        if (step === 3) return selectedEventIds.length > 0;
        if (step === 4) return creditMode === 'none' || !!creditArtist;
        return true;
    };

    const selectedEvent = events.find(e => e.event_id === selectedEventIds[0]) || null;
    const creditArtistId = creditMode === 'assign' && creditArtist ? creditArtist.id : null;

    // createImage / updateImage body fields for this assignment
    const buildPayload = () => {
        const base = { creditArtistId };
        switch (target) {
            case 'event_flyer_single':
                return { ...base, imageKind: target, entityType: 'event', entityId: selectedEventIds[0], eventIds: [] , photoArtistId: null };
            case 'event_flyer_multiple':
                return { ...base, imageKind: target, entityType: filterEntityType, entityId: filterEntity?.id, eventIds: selectedEventIds, photoArtistId: null };
            case 'event_photo_artist':
                return { ...base, imageKind: target, entityType: 'event', entityId: selectedEventIds[0], eventIds: [], photoArtistId: filterEntity?.id };
            case 'non_event':
                return { ...base, imageKind: target, entityType: directEntityType, entityId: directEntity?.id, eventIds: [], photoArtistId: null };
            default: // 'none' — unassigned
                return { ...base, imageKind: null, entityType: null, entityId: null, eventIds: [], photoArtistId: null };
        }
    };

    // "Assigning to" lines for the Details step
    const summary = () => {
        switch (target) {
            case 'event_flyer_single':
                return [`${TARGET_LABEL[target]}: ${formatEventLine(selectedEvent) || `event #${selectedEventIds[0]}`}`];
            case 'event_flyer_multiple':
                return [`${TARGET_LABEL[target]}: ${selectedEventIds.length} event${selectedEventIds.length !== 1 ? 's' : ''} via ${entityLabel(filterEntityType)}: ${filterEntity?.name || ''}`.trim()];
            case 'event_photo_artist':
                return [
                    `${TARGET_LABEL[target]}: ${filterEntity?.name || ''}`,
                    formatEventLine(selectedEvent) || `event #${selectedEventIds[0]}`,
                ];
            case 'non_event':
                return [`${TARGET_LABEL[target]}: ${entityLabel(directEntityType)} · ${directEntity?.name || ''}`.trim()];
            default:
                return [];
        }
    };

    return {
        target, setTarget, isEventTarget, isMultiple, isPhoto, searchType,
        filterEntityType, setFilterEntityType, filterEntity, setFilterEntity,
        directEntityType, setDirectEntityType, directEntity, setDirectEntity,
        events, eventsLoading, eventsError, eventsRefine, setEventsRefine, filteredEvents,
        selectedEventIds, toggleEvent, selectAllListed,
        creditMode, setCreditMode, creditArtist, setCreditArtist, creditArtistId,
        path, canLeave, buildPayload, summary,
    };
}

// Next / previous step along the wizard's path
function stepAfter(path, step)  { const i = path.indexOf(step); return i >= 0 && i < path.length - 1 ? path[i + 1] : step; }
function stepBefore(path, step) { const i = path.indexOf(step); return i > 0 ? path[i - 1] : null; }

// ── Target step ──────────────────────────────────────────────────────────────
function TargetStep({ value, onChange, allowNone }) {
    const options = allowNone
        ? [...UPLOAD_TARGETS, { key: 'none', label: 'None', hint: 'Upload without assigning — you can assign it later via Edit' }]
        : UPLOAD_TARGETS;
    return (
        <div>
            <label style={S.label}>What is this photo for?</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '6px' }}>
                {options.map(t => {
                    const active = value === t.key;
                    return (
                        <label key={t.key} style={{ ...S.radioCard, ...(active ? S.radioCardActive : {}) }}>
                            <input type="radio" name="upload-target" checked={active} onChange={() => onChange(t.key)} style={S.radioInput} />
                            <span>
                                <span style={{ display: 'block', fontSize: '13px', color: active ? '#bb86fc' : '#e0e0e0', fontWeight: 600 }}>{t.label}</span>
                                <span style={{ display: 'block', fontSize: '11px', color: '#888', marginTop: '2px' }}>{t.hint}</span>
                            </span>
                        </label>
                    );
                })}
            </div>
        </div>
    );
}

// ── Search step ──────────────────────────────────────────────────────────────
function SearchStep({ wiz }) {
    if (wiz.target === 'non_event') {
        return (
            <div>
                <label style={S.label}>Search for</label>
                <div style={{ display: 'flex', gap: '6px', marginBottom: '8px', flexWrap: 'wrap' }}>
                    {NON_EVENT_ENTITY_TYPES.map(t => (
                        <button key={t} onClick={() => { wiz.setDirectEntityType(t); wiz.setDirectEntity(null); }} style={{ ...S.filterChip, ...(wiz.directEntityType === t ? S.filterChipActive : {}), padding: '4px 10px', fontSize: '12px' }}>{entityLabel(t)}</button>
                    ))}
                </div>
                <EntitySearch entityType={wiz.directEntityType} value={wiz.directEntity} onChange={wiz.setDirectEntity} />
            </div>
        );
    }
    if (wiz.isPhoto) {
        return (
            <div>
                <label style={S.label}>Search for events by Artist</label>
                <EntitySearch entityType="artist" artistFilter="performers" value={wiz.filterEntity} onChange={wiz.setFilterEntity} />
                <div style={{ fontSize: '11px', color: '#888', marginTop: '6px' }}>The artist you pick is the one pictured in the photo.</div>
            </div>
        );
    }
    return (
        <div>
            <label style={S.label}>Search for events by</label>
            <div style={{ display: 'flex', gap: '6px', marginBottom: '8px', flexWrap: 'wrap' }}>
                {EVENT_FILTER_TYPES.map(t => (
                    <button key={t} onClick={() => { wiz.setFilterEntityType(t); wiz.setFilterEntity(null); }} style={{ ...S.filterChip, ...(wiz.filterEntityType === t ? S.filterChipActive : {}), padding: '4px 10px', fontSize: '12px' }}>{entityLabel(t)}</button>
                ))}
            </div>
            <EntitySearch
                entityType={wiz.filterEntityType}
                value={wiz.filterEntity}
                onChange={wiz.setFilterEntity}
                excludeParents={wiz.filterEntityType === 'festival'}
                artistFilter={wiz.filterEntityType === 'artist' ? 'performers' : ''}
            />
            {wiz.filterEntity && <div style={{ fontSize: '11px', color: '#40916c', marginTop: '4px' }}>✓ Searching by {entityLabel(wiz.filterEntityType)}: {wiz.filterEntity.name}</div>}
        </div>
    );
}

// ── Events step ──────────────────────────────────────────────────────────────
function EventsStep({ wiz }) {
    return (
        <div>
            <label style={S.label}>
                {wiz.isMultiple ? 'Select events (multiple allowed)' : wiz.isPhoto ? `Select the event ${wiz.filterEntity?.name || 'the artist'} played` : 'Select event'}
            </label>
            <input type="text" value={wiz.eventsRefine} onChange={e => wiz.setEventsRefine(e.target.value)} placeholder="Refine by date, venue, city, artists..." style={{ ...S.input, marginBottom: '4px' }} />
            {wiz.eventsLoading && <div style={{ color: '#888', fontSize: '13px', padding: '8px 0' }}>Loading events…</div>}
            {wiz.eventsError && <div style={{ color: '#e57373', fontSize: '13px' }}>{wiz.eventsError}</div>}
            {!wiz.eventsLoading && !wiz.eventsError && (
                <EventResultsTable
                    events={wiz.filteredEvents}
                    selectedIds={wiz.selectedEventIds}
                    onToggle={wiz.toggleEvent}
                    isMultiple={wiz.isMultiple}
                    onSelectAll={wiz.isMultiple ? wiz.selectAllListed : undefined}
                    byVenue={!wiz.isPhoto && wiz.filterEntityType === 'venue'}
                />
            )}
        </div>
    );
}

// ── Credits step (also used inline on the Edit form) ────────────────────────
// Quick create (2026-10-15): typing a name in the Visual Artist search offers
// "＋ Create new Visual Artist", opening AddEvent.js's Quick Create Artist
// modal with Artist Type locked to Visual Artist and Style required. The new
// artist becomes the credit. Picklists (GET /picklists) load on first use.
let picklistsCache = null;
function CreditsPicker({ mode, onModeChange, artist, onArtistChange, compact = false }) {
    const [createName, setCreateName] = useState(null); // null = modal closed
    const [picklists, setPicklists] = useState(picklistsCache);
    const [picklistError, setPicklistError] = useState('');
    const [searchKey, setSearchKey] = useState(0); // remounts EntitySearch to show a created artist

    const openCreate = async (name) => {
        setPicklistError('');
        if (!picklists) {
            try {
                const res = await axios.get(`${API}/picklists`);
                picklistsCache = res.data;
                setPicklists(res.data);
            } catch {
                setPicklistError('Could not load artist types. Please try again.');
                return;
            }
        }
        setCreateName(name || '');
    };

    const handleCreated = (a) => {
        setCreateName(null);
        onArtistChange({ id: a.id, name: a.name });
        setSearchKey(k => k + 1);
    };

    return (
        <div>
            {!compact && <label style={S.label}>Credits</label>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: compact ? 0 : '6px' }}>
                <label style={{ ...S.radioCard, ...(mode === 'none' ? S.radioCardActive : {}) }}>
                    <input type="radio" name={`credit-mode-${compact ? 'edit' : 'wiz'}`} checked={mode === 'none'} onChange={() => onModeChange('none')} style={S.radioInput} />
                    <span style={{ fontSize: '13px', color: mode === 'none' ? '#bb86fc' : '#e0e0e0' }}>No Credit to Assign</span>
                </label>
                <label style={{ ...S.radioCard, ...(mode === 'assign' ? S.radioCardActive : {}) }}>
                    <input type="radio" name={`credit-mode-${compact ? 'edit' : 'wiz'}`} checked={mode === 'assign'} onChange={() => onModeChange('assign')} style={S.radioInput} />
                    <span style={{ fontSize: '13px', color: mode === 'assign' ? '#bb86fc' : '#e0e0e0' }}>
                        Assign Credit to Artist <span style={{ color: '#888', fontSize: '11px' }}>(Graphic Designer, Illustrator, Photographer)</span>
                    </span>
                </label>
            </div>
            {mode === 'assign' && (
                <div style={{ marginTop: '8px' }}>
                    <EntitySearch
                        key={searchKey}
                        entityType="artist"
                        artistFilter="visual"
                        value={artist}
                        onChange={onArtistChange}
                        placeholder="Search Visual Artists by name…"
                        onCreateItem={openCreate}
                        createLabel="Create new Visual Artist"
                    />
                    {picklistError && <div style={{ marginTop: '5px', fontSize: '11px', color: '#cf6679' }}>{picklistError}</div>}
                </div>
            )}
            {createName !== null && picklists && (
                <QuickCreateArtistModal
                    picklists={picklists}
                    initialName={createName}
                    lockedTypeValue={VISUAL_ARTIST_TYPE}
                    onCreated={handleCreated}
                    onClose={() => setCreateName(null)}
                />
            )}
        </div>
    );
}

// ── "Assigning to" + Credit summary (Details step) ──────────────────────────
function AssignmentSummary({ lines, creditName }) {
    return (
        <div style={{ background: '#252525', border: '1px solid #333', borderRadius: '8px', padding: '10px 14px', fontSize: '12px', color: '#aaa' }}>
            <div style={S.summaryHeading}>Assigning to</div>
            {lines.length === 0
                ? <div style={{ color: '#777', fontStyle: 'italic' }}>Not assigned — you can assign this photo later via Edit.</div>
                : lines.map((l, i) => <div key={i} style={{ color: i === 0 ? '#e0e0e0' : '#aaa' }}>{l}</div>)}
            <div style={{ ...S.summaryHeading, marginTop: '10px' }}>Credit</div>
            <div style={{ color: creditName ? '#e0e0e0' : '#777' }}>{creditName || 'None'}</div>
        </div>
    );
}

// Modal width: wide for the Events step, standard otherwise
const wizardWidth = (step) => (step === 3 ? '1125px' : '650px');
const wizardGrid = (isMobile, step) => ({
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : (step === 3 ? '220px 1fr' : '1fr 1fr'),
    gap: isMobile ? '16px' : '24px',
    alignItems: 'start',
});

const STEP_LABELS = { 0: 'Upload', 1: 'Target', 2: 'Search', 3: 'Events', 4: 'Credits', 5: 'Details' };

// ── Edit Modal ────────────────────────────────────────────────────────────────
// Default view: Caption, Credit, Assignment summary, Visibility. "Change
// Assignment" opens the same Target → Search → Events → Credits → Details
// wizard as Upload, pre-filled with the image's current target and credit.
function EditModal({ image, onClose, onSaved, onDeleted }) {
    const isMobile = useIsMobile();
    const isUnassigned = !image.entity_type;
    const initialCredit = image.credit_artist_id
        ? { id: image.credit_artist_id, name: image.credit_artist_name || `Artist #${image.credit_artist_id}` }
        : null;

    const [showWizard, setShowWizard] = useState(false);

    // ── Simple form state ──────────────────────────────────────────────────
    const [caption, setCaption] = useState(image.caption || '');
    const [isPublic, setIsPublic] = useState(image.is_public !== false);
    const [creditMode, setCreditMode] = useState(initialCredit ? 'assign' : 'none');
    const [creditArtist, setCreditArtist] = useState(initialCredit);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [error, setError] = useState('');

    // ── Wizard state ───────────────────────────────────────────────────────
    const wiz = useAssignmentWizard({
        initialTarget: EVENT_KINDS.includes(image.image_kind) || image.image_kind === 'non_event' ? image.image_kind : '',
        initialCredit,
    });
    const [wizStep, setWizStep] = useState(1);
    const [wizCaption, setWizCaption] = useState(image.caption || '');
    const [wizIsPublic, setWizIsPublic] = useState(image.is_public !== false);

    const creditIdFor = (mode, artist) => (mode === 'assign' && artist ? artist.id : null);
    const creditPending = creditMode === 'assign' && !creditArtist;

    const handleSave = async () => {
        if (creditPending) { setError('Pick a Visual Artist, or choose No Credit to Assign.'); return; }
        setSaving(true); setError('');
        try {
            const token = await getIdToken();
            // Caption / Credit / Visibility only — the assignment is untouched;
            // use "Change Assignment" to move this photo to a new target.
            const body = { caption: caption.trim() || null, isPublic };
            const newCreditId = creditIdFor(creditMode, creditArtist);
            if (newCreditId !== (image.credit_artist_id ?? null)) body.creditArtistId = newCreditId;
            const res = await axios.patch(`${API}/images/${image.id}`, body, { headers: { Authorization: token } });
            onSaved({ ...res.data.image, credit_artist_name: newCreditId ? creditArtist?.name : null });
            onClose();
        } catch (e) {
            setError(e.response?.data?.error || 'Save failed.');
        } finally { setSaving(false); }
    };

    const handleWizardSave = async () => {
        setSaving(true); setError('');
        try {
            const token = await getIdToken();
            const p = wiz.buildPayload();
            const res = await axios.patch(`${API}/images/${image.id}`, {
                caption:       wizCaption.trim() || null,
                isPublic:      wizIsPublic,
                imageKind:     p.imageKind,
                entityType:    p.entityType || null,
                entityId:      p.entityId   || null,
                eventIds:      p.eventIds,
                photoArtistId: p.photoArtistId,
                creditArtistId: p.creditArtistId,
            }, { headers: { Authorization: token } });
            onSaved({ ...res.data.image, becameDefault: !!res.data.becameDefault });
            onClose();
        } catch (e) {
            setError(e.response?.data?.error || 'Save failed.');
        } finally { setSaving(false); }
    };

    const handleDelete = async () => {
        setDeleting(true); setError('');
        try {
            const token = await getIdToken();
            await axios.delete(`${API}/images/${image.id}`, { headers: { Authorization: token } });
            onDeleted(image.id);
            onClose();
        } catch (e) {
            setError(e.response?.data?.error || 'Delete failed.');
        } finally { setDeleting(false); }
    };

    const deleteControls = (
        <div style={{ borderTop: '1px solid #333', paddingTop: '12px' }}>
            {!confirmDelete ? <button onClick={() => setConfirmDelete(true)} style={S.deleteBtn}>🗑️ Delete Photo</button> : (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <span style={{ fontSize: '13px', color: '#e57373' }}>Delete permanently?</span>
                    <button onClick={handleDelete} disabled={deleting} style={{ ...S.deleteBtn, opacity: deleting ? 0.6 : 1 }}>{deleting ? 'Deleting…' : 'Yes, Delete'}</button>
                    <button onClick={() => setConfirmDelete(false)} style={S.ghostBtn}>Cancel</button>
                </div>
            )}
        </div>
    );

    // ── Wizard UI ────────────────────────────────────────────────────────────
    if (showWizard) {
        const path = wiz.path;
        const next = () => setWizStep(stepAfter(path, wizStep));
        const back = () => { const b = stepBefore(path, wizStep); if (b === null) setShowWizard(false); else setWizStep(b); };
        const nextLabel = wizStep === 2 && wiz.isEventTarget ? 'Find Events →' : 'Next →';
        return (
            <div style={overlayStyle(isMobile)} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
                <div style={modalStyle(isMobile, wizardWidth(wizStep))}>
                    <div style={S.modalHeader}>
                        <span style={{ fontSize: '18px', fontWeight: 600 }}>{isUnassigned ? 'Assign Photo' : 'Change Assignment'}</span>
                        <button onClick={onClose} style={S.closeBtn}>✕</button>
                    </div>

                    <StepIndicator labels={path.map(n => STEP_LABELS[n])} displayStep={Math.max(0, path.indexOf(wizStep))} isMobile={isMobile} />

                    <div style={wizardGrid(isMobile, wizStep)}>
                        <div>
                            <ZoomablePreview src={image.image_url} alt={image.caption || ''} style={{ maxHeight: isMobile ? '200px' : '320px' }} />
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            {wizStep === 1 && <TargetStep value={wiz.target} onChange={wiz.setTarget} allowNone={false} />}
                            {wizStep === 2 && <SearchStep wiz={wiz} />}
                            {wizStep === 3 && <EventsStep wiz={wiz} />}
                            {wizStep === 4 && <CreditsPicker mode={wiz.creditMode} onModeChange={wiz.setCreditMode} artist={wiz.creditArtist} onArtistChange={wiz.setCreditArtist} />}

                            {wizStep === 5 && (
                                <>
                                    <AssignmentSummary lines={wiz.summary()} creditName={wiz.creditArtistId ? wiz.creditArtist?.name : null} />
                                    <div>
                                        <label style={S.label}>Caption (optional)</label>
                                        <input type="text" value={wizCaption} onChange={e => setWizCaption(e.target.value)} placeholder="Describe this photo..." style={S.input} maxLength={280} />
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <label style={{ ...S.label, margin: 0 }}>Visibility</label>
                                        <button onClick={() => setWizIsPublic(v => !v)} style={{ ...S.toggleBtn, backgroundColor: wizIsPublic ? '#2d6a4f' : '#444', border: `1px solid ${wizIsPublic ? '#40916c' : '#555'}` }}>{wizIsPublic ? '🌐 Public' : '🔒 Private'}</button>
                                        <span style={{ fontSize: '12px', color: '#888' }}>{wizIsPublic ? 'Visible on the entity page' : 'Only visible to you'}</span>
                                    </div>
                                    {error && <div style={{ ...S.errorMsg, marginTop: '4px' }}>{error}</div>}
                                    <button onClick={handleWizardSave} disabled={saving} style={{ ...S.primaryBtn, opacity: saving ? 0.6 : 1, width: '100%' }}>{saving ? 'Saving…' : '💾 Save Assignment'}</button>
                                </>
                            )}

                            {wizStep < 5 && (
                                <button onClick={next} disabled={!wiz.canLeave(wizStep)} style={{ ...S.primaryBtn, width: '100%', opacity: wiz.canLeave(wizStep) ? 1 : 0.5 }}>{nextLabel}</button>
                            )}
                            <button onClick={back} style={S.ghostBtn}>{wizStep === path[0] ? '← Cancel' : '← Back'}</button>
                            {wizStep === 5 && deleteControls}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // ── Simple form UI (default Edit view for every image) ───────────────────
    const kindLabel = TARGET_LABEL[image.image_kind];
    const currentAssignmentLabel = image.entity_type
        ? `${kindLabel || entityLabel(image.entity_type)}: ${image.image_kind === 'event_photo_artist' && image.photo_artist_name
            ? `${image.photo_artist_name} · ${image.entity_name || `#${image.entity_id}`}`
            : (image.entity_name || `#${image.entity_id}`)}`
        : 'Unassigned';

    return (
        <div style={overlayStyle(isMobile)} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div style={isMobile ? { ...S.modal, ...Smobile.modal } : { ...S.modal, maxWidth: '650px' }}>
                <div style={S.modalHeader}>
                    <span style={{ fontSize: '18px', fontWeight: 600 }}>Edit Photo</span>
                    <button onClick={onClose} style={S.closeBtn}>✕</button>
                </div>

                <div style={isMobile ? { ...S.twoCol, ...Smobile.twoCol } : S.twoCol}>
                    <div style={S.twoColLeft}>
                        <ZoomablePreview src={image.image_url} alt={image.caption || ''} style={{ maxHeight: isMobile ? '200px' : '380px' }} />
                    </div>
                    <div style={{ ...S.twoColRight, gap: '12px' }}>
                        <div>
                            <label style={S.label}>Caption (optional)</label>
                            <input type="text" value={caption} onChange={e => setCaption(e.target.value)} placeholder="Describe this photo..." style={S.input} maxLength={280} />
                        </div>
                        <div>
                            <label style={S.label}>Assignment</label>
                            <div style={{ fontSize: '13px', color: '#ccc', marginBottom: '6px' }}>{currentAssignmentLabel}</div>
                            <button onClick={() => { setWizStep(1); setShowWizard(true); }} style={{ ...S.ghostBtn, width: '100%' }}>🔀 Change Assignment</button>
                        </div>
                        <div>
                            <label style={S.label}>Credit</label>
                            <CreditsPicker compact mode={creditMode} onModeChange={setCreditMode} artist={creditArtist} onArtistChange={setCreditArtist} />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <label style={{ ...S.label, margin: 0 }}>Visibility</label>
                            <button onClick={() => setIsPublic(v => !v)} style={{ ...S.toggleBtn, backgroundColor: isPublic ? '#2d6a4f' : '#444', border: `1px solid ${isPublic ? '#40916c' : '#555'}` }}>{isPublic ? '🌐 Public' : '🔒 Private'}</button>
                            <span style={{ fontSize: '12px', color: '#888' }}>{isPublic ? 'Visible on the entity page' : 'Only visible to you'}</span>
                        </div>
                        {error && <div style={{ ...S.errorMsg, marginTop: '4px' }}>{error}</div>}
                        <button onClick={handleSave} disabled={saving} style={{ ...S.primaryBtn, opacity: saving ? 0.6 : 1, width: '100%' }}>{saving ? 'Saving…' : '💾 Save Changes'}</button>
                        {deleteControls}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ── Upload Modal ─────────────────────────────────────────────────────────────
//
// Entry Point A (global, from My Images):
//   Upload → Target → Search → Events → Credits → Details (steps 0–5); the
//   path shortens for Non-Event (no Events) and None (no Search/Events).
// Entry Point B (per-record Add Photo on Event/Artist/Festival/Member/
//   Promoter/Venue pages), target locked to that record:
//   - non-event record: Upload → Credits → Details (Non-Event Image)
//   - event:            Upload → Target (Event Flyer – Single / Event Photo –
//                       Artist) → [Artist, picked from the event's lineup] →
//                       Credits → Details
//
// Props:
//   presetEntityType / presetEntityId / presetEntityName — Entry Point B scoping
//   onClose / onUploaded — standard callbacks
//
function UploadModal({ onClose, onUploaded, presetEntityType, presetEntityId, presetEntityName }) {
    const isMobile = useIsMobile();
    const isPreset = !!(presetEntityType && presetEntityId);
    const isEventPreset = isPreset && presetEntityType === 'event';

    // step: 0 Upload, 1 Target, 2 Search (B event: Artist), 3 Events, 4 Credits, 5 Details
    const [step, setStep] = useState(0);

    // ── Capture state (step 0) ──────────────────────────────────────────────
    const [captureMode, setCaptureMode] = useState('file'); // 'file' | 'camera' | 'preview'
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);
    const fileInputRef = useRef(null);

    // ── Assignment (Entry Point A) ─────────────────────────────────────────
    const wiz = useAssignmentWizard({ initialTarget: '' });

    // ── Entry Point B on an event: Flyer vs Photo + lineup artist ───────────
    const [presetKind, setPresetKind] = useState('event_flyer_single');
    const [lineup, setLineup] = useState([]);           // [{ artist_id, artist_name }]
    const [lineupLoading, setLineupLoading] = useState(false);
    const [lineupError, setLineupError] = useState('');
    const [photoArtistId, setPhotoArtistId] = useState(null);

    // ── Details ────────────────────────────────────────────────────────────
    const [caption, setCaption] = useState('');
    const [isPublic, setIsPublic] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');

    // ── Camera helpers ─────────────────────────────────────────────────────
    const startCamera = useCallback(async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: false });
            streamRef.current = stream;
            if (videoRef.current) videoRef.current.srcObject = stream;
        } catch {
            setError('Camera access denied or unavailable.');
            setCaptureMode('file');
        }
    }, []);

    const stopCamera = useCallback(() => {
        if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    }, []);

    useEffect(() => {
        if (captureMode === 'camera') startCamera();
        else stopCamera();
        return stopCamera;
    }, [captureMode, startCamera, stopCamera]);

    // Lineup for an event's Event Photo – Artist (Entry Point B)
    useEffect(() => {
        if (!isEventPreset || presetKind !== 'event_photo_artist' || lineup.length > 0) return;
        let cancelled = false;
        setLineupLoading(true); setLineupError('');
        axios.get(`${API}/events/${presetEntityId}`)
            .then(res => { if (!cancelled) setLineup(res.data.artists || []); })
            .catch(() => { if (!cancelled) setLineupError('Failed to load the lineup.'); })
            .finally(() => { if (!cancelled) setLineupLoading(false); });
        return () => { cancelled = true; };
    }, [isEventPreset, presetKind, presetEntityId, lineup.length]);

    // Step path for the current entry point / target
    const path = !isPreset
        ? [0, ...wiz.path]
        : isEventPreset
            ? (presetKind === 'event_photo_artist' ? [0, 1, 2, 4, 5] : [0, 1, 4, 5])
            : [0, 4, 5];
    const labelFor = (n) => (isEventPreset && n === 2 ? 'Artist' : STEP_LABELS[n]);

    const canLeave = (n) => {
        if (!isPreset) return wiz.canLeave(n);
        if (n === 2) return !!photoArtistId;
        if (n === 4) return wiz.creditMode === 'none' || !!wiz.creditArtist;
        return true;
    };

    const capturePhoto = () => {
        const video = videoRef.current; const canvas = canvasRef.current;
        if (!video || !canvas) return;
        canvas.width = video.videoWidth; canvas.height = video.videoHeight;
        canvas.getContext('2d').drawImage(video, 0, 0);
        canvas.toBlob(blob => {
            if (!blob) return;
            const f = new File([blob], `capture-${Date.now()}.jpg`, { type: 'image/jpeg' });
            setFile(f); setPreview(URL.createObjectURL(f)); stopCamera(); setCaptureMode('preview');
            setStep(stepAfter(path, 0));
        }, 'image/jpeg', 0.92);
    };

    const handleFileSelect = (f) => {
        if (!f) return;
        if (f.size > MAX_FILE_BYTES) { setError('File exceeds 4 MB limit.'); return; }
        if (!f.type.startsWith('image/')) { setError('Only image files are allowed.'); return; }
        setError(''); setFile(f); setPreview(URL.createObjectURL(f)); setCaptureMode('preview');
        setStep(stepAfter(path, 0));
    };

    const handleDrop = (e) => {
        e.preventDefault();
        handleFileSelect(e.dataTransfer.files?.[0]);
    };

    const resetPhoto = () => {
        setFile(null); setPreview(null); setCaptureMode('file'); setStep(0);
    };

    // ── Upload handler ─────────────────────────────────────────────────────
    const toBase64 = f => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(f);
    });

    const buildPayload = () => {
        if (!isPreset) return wiz.buildPayload();
        const creditArtistId = wiz.creditArtistId;
        if (isEventPreset) {
            return presetKind === 'event_photo_artist'
                ? { imageKind: 'event_photo_artist', entityType: 'event', entityId: presetEntityId, eventIds: null, photoArtistId, creditArtistId }
                : { imageKind: 'event_flyer_single', entityType: 'event', entityId: presetEntityId, eventIds: null, photoArtistId: null, creditArtistId };
        }
        return { imageKind: 'non_event', entityType: presetEntityType, entityId: presetEntityId, eventIds: null, photoArtistId: null, creditArtistId };
    };

    const handleUpload = async () => {
        if (!file) return;
        setUploading(true); setError('');
        try {
            const token = await getIdToken();
            const data = await toBase64(file);
            const uploadRes = await axios.post(`${API}/images/upload`,
                { filename: file.name, contentType: file.type, data },
                { headers: { Authorization: token } }
            );
            const { s3Key } = uploadRes.data;
            const p = buildPayload();

            const createRes = await axios.post(`${API}/images`, {
                s3Key,
                caption:        caption.trim() || null,
                isPublic,
                imageKind:      p.imageKind,
                entityType:     p.entityType || null,
                entityId:       p.entityId   || null,
                eventIds:       p.eventIds && p.eventIds.length > 0 ? p.eventIds : null,
                photoArtistId:  p.photoArtistId || null,
                creditArtistId: p.creditArtistId || null,
            }, { headers: { Authorization: token } });

            onUploaded({ ...createRes.data.image, becameDefault: !!createRes.data.becameDefault });
            onClose();
        } catch (e) {
            setError(e.response?.data?.error || 'Upload failed. Please try again.');
        } finally {
            setUploading(false);
        }
    };

    // ── Details summary ────────────────────────────────────────────────────
    const summaryLines = () => {
        if (!isPreset) return wiz.summary();
        if (isEventPreset) {
            if (presetKind === 'event_photo_artist') {
                const a = lineup.find(x => x.artist_id === photoArtistId);
                return [`${TARGET_LABEL.event_photo_artist}: ${a?.artist_name || ''}`, presetEntityName];
            }
            return [`${TARGET_LABEL.event_flyer_single}: ${presetEntityName}`];
        }
        return [`${TARGET_LABEL.non_event}: ${entityLabel(presetEntityType)} · ${presetEntityName}`];
    };

    const nextStep = () => setStep(stepAfter(path, step));
    const prevStep = () => { const b = stepBefore(path, step); if (b === null || b === 0) resetPhoto(); else setStep(b); };
    const nextLabel = !isPreset && step === 2 && wiz.isEventTarget ? 'Find Events →' : 'Next →';

    return (
        <div style={overlayStyle(isMobile)} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div style={modalStyle(isMobile, wizardWidth(step))}>

                {/* Header */}
                <div style={S.modalHeader}>
                    <span style={{ fontSize: '18px', fontWeight: 600 }}>Upload Photo</span>
                    <button onClick={onClose} style={S.closeBtn}>✕</button>
                </div>

                {file && (
                    <StepIndicator labels={path.map(labelFor)} displayStep={Math.max(0, path.indexOf(step))} isMobile={isMobile} />
                )}

                {/* ── Step 0: Upload ─────────────────────────────────────── */}
                {step === 0 && (
                    <>
                        {captureMode !== 'preview' && (
                            <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
                                <button onClick={() => setCaptureMode('file')} style={{ ...S.modeBtn, ...(captureMode === 'file' ? S.modeBtnActive : {}) }}>📁 From Device</button>
                                <button onClick={() => setCaptureMode('camera')} style={{ ...S.modeBtn, ...(captureMode === 'camera' ? S.modeBtnActive : {}) }}>📷 Camera</button>
                            </div>
                        )}
                        {captureMode === 'file' && (
                            <div style={S.dropZone} onDrop={handleDrop} onDragOver={e => e.preventDefault()} onClick={() => fileInputRef.current?.click()}>
                                <div style={{ fontSize: '32px', marginBottom: '8px' }}>🖼️</div>
                                <div style={{ color: '#aaa', fontSize: '14px' }}>Drag & drop or click to select</div>
                                <div style={{ color: '#666', fontSize: '12px', marginTop: '4px' }}>JPEG, PNG, GIF, WEBP · Max 4 MB</div>
                                <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" style={{ display: 'none' }} onChange={e => handleFileSelect(e.target.files?.[0])} />
                            </div>
                        )}
                        {captureMode === 'camera' && (
                            <div style={{ textAlign: 'center', marginBottom: '12px' }}>
                                <video ref={videoRef} autoPlay playsInline muted style={{ width: '100%', maxHeight: '300px', borderRadius: '8px', objectFit: 'cover' }} />
                                <canvas ref={canvasRef} style={{ display: 'none' }} />
                                <button onClick={capturePhoto} style={{ ...S.primaryBtn, marginTop: '10px' }}>📸 Capture Photo</button>
                            </div>
                        )}
                        {error && <div style={{ ...S.errorMsg, marginTop: '8px' }}>{error}</div>}
                    </>
                )}

                {/* Steps 1–5: preview on the left, step on the right */}
                {step >= 1 && file && (
                    <div style={wizardGrid(isMobile, step)}>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <ZoomablePreview src={preview} alt="Preview" style={{ maxHeight: isMobile ? '200px' : '320px' }} />
                            <button onClick={resetPhoto} style={{ ...S.ghostBtn, marginTop: '8px', fontSize: '12px', width: '100%' }}>
                                ← Choose different photo
                            </button>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                            {/* ── Step 1: Target ── */}
                            {step === 1 && !isPreset && <TargetStep value={wiz.target} onChange={wiz.setTarget} allowNone />}
                            {step === 1 && isEventPreset && (
                                <div>
                                    <label style={S.label}>What is this photo for?</label>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '6px' }}>
                                        {UPLOAD_TARGETS.filter(t => t.key === 'event_flyer_single' || t.key === 'event_photo_artist').map(t => {
                                            const active = presetKind === t.key;
                                            return (
                                                <label key={t.key} style={{ ...S.radioCard, ...(active ? S.radioCardActive : {}) }}>
                                                    <input type="radio" name="preset-kind" checked={active} onChange={() => { setPresetKind(t.key); setPhotoArtistId(null); }} style={S.radioInput} />
                                                    <span>
                                                        <span style={{ display: 'block', fontSize: '13px', color: active ? '#bb86fc' : '#e0e0e0', fontWeight: 600 }}>{t.label}</span>
                                                        <span style={{ display: 'block', fontSize: '11px', color: '#888', marginTop: '2px' }}>{t.hint}</span>
                                                    </span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* ── Step 2: Search (A) / Artist from lineup (B event) ── */}
                            {step === 2 && !isPreset && <SearchStep wiz={wiz} />}
                            {step === 2 && isEventPreset && (
                                <div>
                                    <label style={S.label}>Who is pictured?</label>
                                    {lineupLoading && <div style={{ color: '#888', fontSize: '13px', padding: '8px 0' }}>Loading lineup…</div>}
                                    {lineupError && <div style={{ color: '#e57373', fontSize: '13px' }}>{lineupError}</div>}
                                    {!lineupLoading && !lineupError && lineup.length === 0 && <div style={{ color: '#666', fontSize: '13px' }}>This event has no artists on its lineup.</div>}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '6px', maxHeight: '300px', overflowY: 'auto' }}>
                                        {lineup.map(a => {
                                            const active = photoArtistId === a.artist_id;
                                            return (
                                                <label key={a.artist_id} style={{ ...S.radioCard, ...(active ? S.radioCardActive : {}) }}>
                                                    <input type="radio" name="pictured-artist" checked={active} onChange={() => setPhotoArtistId(a.artist_id)} style={S.radioInput} />
                                                    <span style={{ fontSize: '13px', color: active ? '#bb86fc' : '#e0e0e0' }}>{a.artist_name}</span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* ── Step 3: Events (A only) ── */}
                            {step === 3 && <EventsStep wiz={wiz} />}

                            {/* ── Step 4: Credits ── */}
                            {step === 4 && <CreditsPicker mode={wiz.creditMode} onModeChange={wiz.setCreditMode} artist={wiz.creditArtist} onArtistChange={wiz.setCreditArtist} />}

                            {/* ── Step 5: Details ── */}
                            {step === 5 && (
                                <>
                                    <AssignmentSummary lines={summaryLines()} creditName={wiz.creditArtistId ? wiz.creditArtist?.name : null} />
                                    <div>
                                        <label style={S.label}>Caption (optional)</label>
                                        <input type="text" value={caption} onChange={e => setCaption(e.target.value)} placeholder="Describe this photo..." style={S.input} maxLength={280} />
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <label style={{ ...S.label, margin: 0 }}>Visibility</label>
                                        <button onClick={() => setIsPublic(v => !v)} style={{ ...S.toggleBtn, backgroundColor: isPublic ? '#2d6a4f' : '#444', border: `1px solid ${isPublic ? '#40916c' : '#555'}` }}>
                                            {isPublic ? '🌐 Public' : '🔒 Private'}
                                        </button>
                                        <span style={{ fontSize: '12px', color: '#888' }}>{isPublic ? 'Visible on the entity page' : 'Only visible to you'}</span>
                                    </div>
                                    {error && <div style={{ ...S.errorMsg, marginTop: '4px' }}>{error}</div>}
                                    <button onClick={handleUpload} disabled={uploading} style={{ ...S.primaryBtn, opacity: uploading ? 0.6 : 1, width: '100%' }}>
                                        {uploading ? 'Uploading…' : '⬆️  Upload Photo'}
                                    </button>
                                </>
                            )}

                            {step < 5 && (
                                <button onClick={nextStep} disabled={!canLeave(step)} style={{ ...S.primaryBtn, width: '100%', opacity: canLeave(step) ? 1 : 0.5 }}>{nextLabel}</button>
                            )}
                            <button onClick={prevStep} style={S.ghostBtn}>← Back</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// ── Pagination (spec §5.4) ────────────────────────────────────────────────────
// Same markup and styles as ArtistList.js (Browse Artists): ← Prev, numbered
// buttons (first, last, current ±2, ellipses), Next →, "1–50 of N".
// Rendered above and below the grid (D3).
function PageNav({ page, totalPages, totalItems, onChange }) {
    return (
        <div style={S.pagination}>
            <button
                onClick={() => onChange(Math.max(1, page - 1))}
                disabled={page === 1}
                style={{ ...S.pageBtn, ...(page === 1 ? S.pageBtnDisabled : {}) }}
            >← Prev</button>
            <div style={S.pageNumbers}>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
                    .reduce((acc, p, i, arr) => {
                        if (i > 0 && p - arr[i - 1] > 1) acc.push('...');
                        acc.push(p);
                        return acc;
                    }, [])
                    .map((p, i) => p === '...'
                        ? <span key={`ellipsis-${i}`} style={S.ellipsis}>…</span>
                        : <button key={p} onClick={() => onChange(p)} style={{ ...S.pageBtn, ...(p === page ? S.pageBtnActive : {}) }}>{p}</button>
                    )
                }
            </div>
            <button
                onClick={() => onChange(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                style={{ ...S.pageBtn, ...(page === totalPages ? S.pageBtnDisabled : {}) }}
            >Next →</button>
            <span style={S.pageInfo}>
                {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, totalItems)} of {totalItems.toLocaleString()}
            </span>
        </div>
    );
}

// Chip filter predicate (module-level so useMemo deps stay clean).
function matchesType(img, key) {
    if (key === 'all') return true;
    if (key === 'unassigned') return !img.entity_type;
    return img.entity_type === key;
}

// ── Main Page ─────────────────────────────────────────────────────────────────
// Rebuilt on Browse Artists (ArtistList.js) — SHARC_Spec_My_Images_Search_2026-10-09.
//   Row 1: title + "X of Y" + Upload Photo
//   Row 2: entity-type chips (counts follow the search; visibility follows the library)
//   Row 3: event-aware search + Clear all
//   Row 4: pagination (50 per page), repeated below the grid
export default function MyImages() {
    const isMobile  = useIsMobile();
    const isTablet  = useIsTablet();
    const isWide    = useMediaQuery('(min-width: 1280px)');

    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showUpload, setShowUpload] = useState(false);
    const [editImage, setEditImage] = useState(null);
    const [filterType, setFilterType] = useState('all');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [lightboxImg, setLightboxImg] = useState(null);
    const [hoveredThumb, setHoveredThumb] = useState(null);

    useEffect(() => {
        document.title = 'SHARC - My Images';
        return () => { document.title = 'SHARC - Shows Archive'; };
    }, []);

    // silent = background refresh after upload/save (spec §6.6): no
    // "Loading…" flash, current search/chip/page kept, failures ignored.
    const loadImages = useCallback(async ({ silent = false } = {}) => {
        if (!silent) { setLoading(true); setError(''); }
        try {
            const token = await getIdToken();
            const res = await axios.get(`${API}/images/mine`, { headers: { Authorization: token } });
            setImages(res.data.images || []);
        } catch {
            if (!silent) setError('Failed to load images.');
        } finally {
            if (!silent) setLoading(false);
        }
    }, []);

    useEffect(() => { loadImages(); }, [loadImages]);

    // createImage/updateImage responses carry neither entity_name nor
    // linked_events, so update locally for instant feedback, then refresh
    // in the background to pick up the server-enriched record.
    const handleUploaded = (img) => {
        setImages(prev => [{ ...img, linked_events: [] }, ...prev]);
        loadImages({ silent: true });
    };
    const handleSaved = (img) => {
        setImages(prev => prev.map(i => {
            if (i.id !== img.id) return i;
            const sameTarget = img.entity_type === i.entity_type && String(img.entity_id) === String(i.entity_id);
            return {
                ...i,
                ...img,
                image_url:     i.image_url,
                entity_name:   sameTarget ? i.entity_name : null,
                linked_events: sameTarget ? i.linked_events : [],
            };
        }));
        loadImages({ silent: true });
    };
    const handleDeleted = (id) => setImages(prev => prev.filter(i => i.id !== id));

    // Lightbox ✏️ Edit (D6) — close the lightbox, open the full EditModal
    // (keeps Change Assignment available, unlike ImageQuickEditModal).
    const openEditFromLightbox = () => {
        const img = lightboxImg;
        setLightboxImg(null);
        if (img) setEditImage(img);
    };

    // ── Search (spec §7) ────────────────────────────────────────────────────
    const haystacks = useMemo(() => {
        const map = new Map();
        images.forEach(img => map.set(img.id, buildHaystack(img)));
        return map;
    }, [images]);

    // Multi-word queries AND across tokens: every token must appear somewhere
    // in the image's haystack ("future islands empty bottle").
    const tokens = useMemo(() => normalizeStr(search).split(/\s+/).filter(Boolean), [search]);

    const searchMatched = useMemo(() => {
        if (tokens.length === 0) return images;
        return images.filter(img => {
            const hay = haystacks.get(img.id) || '';
            return tokens.every(t => hay.includes(t));
        });
    }, [images, haystacks, tokens]);

    const filtered = useMemo(
        () => searchMatched.filter(img => matchesType(img, filterType)),
        [searchMatched, filterType]
    );

    // Chip visibility follows the whole library (so the row never shifts while
    // typing); chip counts follow the active search (D5).
    const chipDefs = [
        { key: 'all', label: 'All' },
        { key: 'unassigned', label: 'Unassigned' },
        ...ENTITY_TYPES.map(t => ({ key: t, label: entityLabel(t) + 's' })),
    ];
    const libraryHas = useMemo(() => {
        const has = { all: true, unassigned: images.some(i => !i.entity_type) };
        ENTITY_TYPES.forEach(t => { has[t] = images.some(i => i.entity_type === t); });
        return has;
    }, [images]);
    const counts = useMemo(() => {
        const c = { all: searchMatched.length, unassigned: searchMatched.filter(i => !i.entity_type).length };
        ENTITY_TYPES.forEach(t => { c[t] = searchMatched.filter(i => i.entity_type === t).length; });
        return c;
    }, [searchMatched]);

    // ── Pagination ──────────────────────────────────────────────────────────
    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

    // Clamp after a delete/refresh leaves the current page past the end.
    useEffect(() => {
        if (page > totalPages) setPage(totalPages);
    }, [page, totalPages]);

    const currentPage = Math.min(page, totalPages);
    const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
    const showPagination = filtered.length > PAGE_SIZE;

    const hasFilters = search.trim() !== '' || filterType !== 'all';
    const clearFilters = () => { setSearch(''); setFilterType('all'); setPage(1); };

    // ── Grid columns (spec §5.5 / D2) ───────────────────────────────────────
    const columns = isMobile ? 2 : isTablet ? 3 : isWide ? 5 : 4;
    const gridStyle = {
        ...S.grid,
        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
        ...(isMobile ? Smobile.grid : {}),
    };

    return (
        <div>
            {/* ── Row 1: Header ── */}
            <div style={isMobile ? { ...S.header, ...Smobile.header } : S.header}>
                <h1 style={S.title}>My Images</h1>
                <div style={S.headerRight}>
                    {!loading && !error && (
                        <span style={S.count}>{filtered.length.toLocaleString()} of {images.length.toLocaleString()}</span>
                    )}
                    <button onClick={() => setShowUpload(true)} style={S.addButton}>+ Upload Photo</button>
                </div>
            </div>

            {/* ── Row 2: Type chips ── */}
            <div style={isMobile ? { ...S.filterBar, ...Smobile.chipRow } : S.filterBar}>
                {chipDefs.filter(({ key }) => libraryHas[key]).map(({ key, label }) => (
                    <button
                        key={key}
                        onClick={() => { setFilterType(key); setPage(1); }}
                        style={{ ...S.filterChip, ...(filterType === key ? S.filterChipActive : {}), ...(isMobile ? Smobile.chip : {}) }}
                    >
                        {label}<span style={S.chipCount}>{counts[key] || 0}</span>
                    </button>
                ))}
            </div>

            {/* ── Row 3: Event-aware search ── */}
            <div style={S.filterBar}>
                <input
                    type="text"
                    placeholder="Search by artist, venue, festival, promoter, city, date or caption..."
                    value={search}
                    onChange={e => { setSearch(e.target.value); setPage(1); }}
                    style={S.searchInput}
                />
                {hasFilters && (
                    <button onClick={clearFilters} style={isMobile ? { ...S.clearBtn, ...Smobile.clearBtn } : S.clearBtn}>✕ Clear all</button>
                )}
            </div>

            {/* ── Row 4: Pagination (top) ── */}
            {!loading && !error && showPagination && (
                <PageNav page={currentPage} totalPages={totalPages} totalItems={filtered.length} onChange={setPage} />
            )}

            {loading && <div style={S.message}>Loading images...</div>}
            {!loading && error && <div style={S.errorMsg}>{error}</div>}
            {!loading && !error && filtered.length === 0 && (
                <div style={S.message}>
                    {images.length === 0
                        ? 'No photos yet. Click "+ Upload Photo" to get started!'
                        : 'No images match your search.'}
                </div>
            )}

            {!loading && !error && pageItems.length > 0 && (
                <div style={gridStyle}>
                    {pageItems.map(img => (
                        <div key={img.id} style={S.card}>
                            <div
                                style={S.thumb}
                                onClick={() => setLightboxImg(img)}
                                onMouseEnter={() => setHoveredThumb(img.id)}
                                onMouseLeave={() => setHoveredThumb(null)}
                                title="View full size"
                            >
                                <img src={img.image_url} alt={img.caption || ''} style={S.thumbImg} loading="lazy" />
                                {!img.is_public && <span style={S.privateBadge}>🔒</span>}
                                <ZoomHint visible={hoveredThumb === img.id} />
                            </div>
                            <div style={S.cardBody}>
                                {img.caption && <div style={S.caption} title={img.caption}>{img.caption}</div>}
                                {(img.photo_artist_name || img.credit_artist_name) && (
                                    <div style={S.creditLine} title={[img.photo_artist_name && `Pictured: ${img.photo_artist_name}`, img.credit_artist_name && `Credit: ${img.credit_artist_name}`].filter(Boolean).join(' · ')}>
                                        {/* 2026-10-19: words instead of the camera / palette icons, names link to the artist */}
                                        {img.photo_artist_name && <>Pictured: <ArtistNameLink name={img.photo_artist_name} slug={img.photo_artist_slug} id={img.photo_artist_id} /></>}
                                        {img.photo_artist_name && img.credit_artist_name && ' · '}
                                        {img.credit_artist_name && <>Credit: <ArtistNameLink name={img.credit_artist_name} slug={img.credit_artist_slug} id={img.credit_artist_id} /></>}
                                    </div>
                                )}
                                <div style={isMobile ? { ...S.cardMeta, ...Smobile.cardMeta } : S.cardMeta}>
                                    {img.entity_type ? (
                                        <span style={S.entityBadge}>
                                            {entityLabel(img.entity_type)}: {img.entity_name || `#${img.entity_id}`}
                                        </span>
                                    ) : (
                                        <span style={{ color: '#555', fontSize: '12px' }}>Unassigned</span>
                                    )}
                                    <span style={{ color: '#555', fontSize: '11px' }}>{formatDate(img.created_at)}</span>
                                </div>
                                <button onClick={() => setEditImage(img)} style={isMobile ? { ...S.editBtn, ...Smobile.editBtn } : S.editBtn}>Edit</button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* ── Pagination (bottom, D3) — scrolls back to the top on change ── */}
            {!loading && !error && showPagination && (
                <div style={S.bottomPagination}>
                    <PageNav
                        page={currentPage}
                        totalPages={totalPages}
                        totalItems={filtered.length}
                        onChange={p => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    />
                </div>
            )}

            {lightboxImg && (
                <ImageLightbox
                    src={lightboxImg.image_url}
                    alt={lightboxImg.caption || ''}
                    caption={lightboxImg.caption}
                    onClose={() => setLightboxImg(null)}
                    onEdit={openEditFromLightbox}
                    imageMeta={lightboxImg}
                />
            )}

            {showUpload && <UploadModal onClose={() => setShowUpload(false)} onUploaded={handleUploaded} />}
            {editImage && <EditModal image={editImage} onClose={() => setEditImage(null)} onSaved={handleSaved} onDeleted={handleDeleted} />}
        </div>
    );
}

// Export UploadModal so detail pages can use it inline
export { UploadModal };

// ── Styles ────────────────────────────────────────────────────────────────────
const S = {
    // Page header / filter rows / pagination — copied from ArtistList.js (Browse Artists)
    header:      { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
    title:       { fontSize: '32px', fontWeight: 'bold', color: '#e0e0e0', margin: 0 },
    headerRight: { display: 'flex', alignItems: 'center', gap: '16px' },
    count:       { fontSize: '13px', color: '#666' },
    addButton:   { backgroundColor: '#bb86fc', color: '#121212', border: 'none', padding: '8px 18px', borderRadius: '4px', fontWeight: 'bold', fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit' },
    filterBar:   { display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '20px', alignItems: 'center' },
    searchInput: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', padding: '9px 14px', fontSize: '14px', color: '#e0e0e0', flex: '1 1 auto', minWidth: '200px', boxSizing: 'border-box' },
    clearBtn:    { backgroundColor: 'transparent', color: '#888', border: '1px solid #444', borderRadius: '6px', padding: '9px 12px', fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap' },
    message:     { textAlign: 'center', color: '#aaa', fontSize: '16px', padding: '60px 0' },
    pagination:     { display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' },
    pageNumbers:    { display: 'flex', gap: '4px', alignItems: 'center' },
    pageBtn:        { backgroundColor: '#2c2c2c', color: '#e0e0e0', border: '1px solid #444', borderRadius: '4px', padding: '5px 10px', fontSize: '13px', cursor: 'pointer', minWidth: '36px', textAlign: 'center' },
    pageBtnActive:  { backgroundColor: '#bb86fc', color: '#121212', border: '1px solid #bb86fc', fontWeight: 700 },
    pageBtnDisabled:{ opacity: 0.35, cursor: 'default' },
    ellipsis:       { color: '#555', padding: '0 4px', fontSize: '13px' },
    pageInfo:       { fontSize: '12px', color: '#666', marginLeft: '8px' },
    bottomPagination: { marginTop: '20px' },

    // Chips / grid / cards (unchanged from the previous My Images, except
    // gridTemplateColumns, which is now set per breakpoint at render time)
    filterChip: { display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 12px', borderRadius: '20px', border: '1px solid #444', background: '#2c2c2c', color: '#aaa', fontSize: '13px', cursor: 'pointer' },
    filterChipActive: { border: '1px solid #bb86fc', background: '#2a2040', color: '#bb86fc' },
    chipCount: { background: '#333', borderRadius: '10px', padding: '1px 6px', fontSize: '11px', color: '#888' },
    grid: { display: 'grid', gap: '16px' },
    card: { background: '#1e1e1e', border: '1px solid #333', borderRadius: '10px', overflow: 'hidden', display: 'flex', flexDirection: 'column', minWidth: 0 },
    thumb: { position: 'relative', width: '100%', paddingTop: '75%', overflow: 'hidden', cursor: 'zoom-in', background: '#111' },
    thumbImg: { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' },
    privateBadge: { position: 'absolute', top: '6px', right: '6px', fontSize: '14px', background: 'rgba(0,0,0,0.6)', borderRadius: '4px', padding: '2px 4px' },
    cardBody: { padding: '10px 12px 12px', display: 'flex', flexDirection: 'column', gap: '6px', flex: 1 },
    caption: { fontSize: '13px', color: '#ccc', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    cardMeta: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '6px', flexWrap: 'wrap' },
    entityBadge: { fontSize: '11px', background: '#2a2040', color: '#bb86fc', border: '1px solid #3d3060', borderRadius: '4px', padding: '2px 6px', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', boxSizing: 'border-box' },
    editBtn: { marginTop: '4px', padding: '5px 10px', background: 'transparent', border: '1px solid #444', borderRadius: '5px', color: '#aaa', fontSize: '12px', cursor: 'pointer', alignSelf: 'flex-end' },

    // Modal
    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '16px' },
    modal: { background: '#1e1e1e', border: '1px solid #333', borderRadius: '12px', padding: '24px', width: '100%', maxHeight: '90vh', overflowY: 'auto' },
    modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
    closeBtn: { background: 'transparent', border: 'none', color: '#888', fontSize: '18px', cursor: 'pointer', lineHeight: 1 },
    twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', alignItems: 'start' },
    twoColLeft: { display: 'flex', flexDirection: 'column' },
    twoColRight: { display: 'flex', flexDirection: 'column', gap: '4px' },
    dropZone: { border: '2px dashed #444', borderRadius: '10px', padding: '36px 20px', textAlign: 'center', cursor: 'pointer', marginBottom: '16px' },
    label: { display: 'block', fontSize: '12px', color: '#888', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' },
    input: { width: '100%', background: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', color: '#e0e0e0', padding: '8px 10px', fontSize: '14px', boxSizing: 'border-box' },
    primaryBtn: { background: '#bb86fc', color: '#1a1a1a', border: 'none', borderRadius: '6px', padding: '9px 18px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
    ghostBtn: { background: 'transparent', border: '1px solid #555', borderRadius: '6px', color: '#aaa', padding: '7px 14px', fontSize: '13px', cursor: 'pointer' },
    deleteBtn: { background: 'transparent', border: '1px solid #e57373', borderRadius: '6px', color: '#e57373', padding: '7px 14px', fontSize: '13px', cursor: 'pointer' },
    toggleBtn: { borderRadius: '20px', padding: '5px 14px', fontSize: '13px', cursor: 'pointer', color: '#e0e0e0' },
    modeBtn: { flex: 1, background: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', color: '#aaa', padding: '8px', fontSize: '13px', cursor: 'pointer' },
    modeBtnActive: { border: '1px solid #bb86fc', color: '#bb86fc', background: '#2a2040' },
    errorMsg: { color: '#e57373', background: '#2a1a1a', border: '1px solid #6b2c2c', borderRadius: '6px', padding: '8px 12px', fontSize: '13px' },

    // Wizard radio cards + summary (2026-10-12, Visual Artists & Image Credits)
    radioCard: { display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '9px 12px', border: '1px solid #444', borderRadius: '8px', background: '#252525', cursor: 'pointer' },
    radioCardActive: { border: '1px solid #bb86fc', background: '#2a2040' },
    // index.css gives every <input> width: 100% + padding — pin radios to their natural size
    radioInput: { margin: '2px 0 0', accentColor: '#bb86fc', width: 'auto', flex: '0 0 auto', padding: 0 },
    summaryHeading: { color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '11px', marginBottom: '4px' },
    creditLine: { fontSize: '11px', color: '#888', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
};

// ── Mobile style overrides (< 768px) — merged into S at render time only when
// useIsMobile() is true (SHARC_Mobile_Responsive_Spec pattern; My Images
// Search spec §8). Desktop styles above are untouched by these.
const Smobile = {
    // Page
    header:   { flexWrap: 'wrap', gap: '10px' },
    chipRow:  { flexWrap: 'nowrap', overflowX: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: '4px' },
    chip:     { flexShrink: 0, whiteSpace: 'nowrap' },
    clearBtn: { marginLeft: 'auto' },
    grid:     { gap: '10px' },
    cardMeta: { flexDirection: 'column', alignItems: 'flex-start', gap: '4px' },
    editBtn:  { alignSelf: 'stretch', minHeight: '36px', fontSize: '13px' },

    // Modals (UploadModal is shared with all six entity detail pages)
    overlay:  { padding: '8px' },
    modal:    { maxWidth: '100%', padding: '16px', maxHeight: '92vh' },
    twoCol:   { gridTemplateColumns: '1fr', gap: '16px' },
    stepRow:  { flexWrap: 'nowrap', overflowX: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: '4px' },
    eventTableOuter: { overflowX: 'auto', WebkitOverflowScrolling: 'touch' },
    eventTableInner: { minWidth: '620px' },
};
